import { useState, useCallback, useMemo } from 'react';
import { useApi } from '@/shared/hooks/useApi';
import { mapService, MapDataPoint } from '@/services/api/map';
import { Location } from '@/types/models/schema';
import { ApiResponse } from '@/types/api';
import { AxiosInstance } from 'axios';

/**
 * Interface for map data response
 */
export interface MapData {
  guests: MapDataPoint[];
  teams: MapDataPoint[];
  clients: MapDataPoint[];
}

/**
 * Simplified useMap Hook
 * 
 * Provides map data and functionality for the map view
 */
export function useMap() {
  const { 
    data: mapData, 
    isLoading, 
    refetch: fetchMapData,
    error
  } = useApi<MapDataPoint[]>(
    useCallback(async (client: AxiosInstance) => {
      return mapService.getAllLocationData();
    }, [])
  );
  
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  
  const geocodeAddress = useCallback(async (address: string): Promise<ApiResponse<Location>> => {
    const rawResponse = await mapService.geocodeAddress(address);
    
    // Wrap raw response in ApiResponse
    const response: ApiResponse<Location> = {
      data: rawResponse,
      error: rawResponse === null ? new Error(`Failed to geocode address: ${address}`) : null
    };
    
    return response;
  }, []);
  
  // Get location statistics
  const getLocationStatistics = useCallback(async (): Promise<ApiResponse<{
    totalLocations: number;
    countries: number;
    guestCount: number;
    teamCount: number;
    clientCount: number;
  }>> => {
    // mapService.getLocationStatistics already returns ApiResponse<LocationStatistics>
    return mapService.getLocationStatistics();
  }, []);
  
  // Group data points by type
  const groupedMapData = useMemo<MapData>(() => {
    if (!mapData || !mapData.data || mapData.data.length === 0) {
      return { guests: [], teams: [], clients: [] };
    }
    
    const guests = mapData.data.filter((point: MapDataPoint) => point.type === 'guest');
    const teams = mapData.data.filter((point: MapDataPoint) => point.type === 'team');
    const clients = mapData.data.filter((point: MapDataPoint) => point.type === 'client');
    
    return { guests, teams, clients };
  }, [mapData]);
  
  // Memoized data points for better performance
  const allDataPoints = useMemo(() => {
    return mapData?.data || [];
  }, [mapData]);
  
  // Get data point by ID
  const getDataPointById = useCallback((id: string): MapDataPoint | undefined => {
    if (!mapData || !mapData.data || mapData.data.length === 0) return undefined;
    
    return allDataPoints.find((point: MapDataPoint) => point.id === id);
  }, [mapData, allDataPoints]);
  
  // Filter data points by type
  const filterDataPointsByType = useCallback((type: 'guest' | 'team' | 'client'): MapDataPoint[] => {
    if (!groupedMapData) return [];
    
    switch (type) {
      case 'guest':
        return groupedMapData.guests;
      case 'team':
        return groupedMapData.teams;
      case 'client':
        return groupedMapData.clients;
      default:
        return [];
    }
  }, [groupedMapData]);
  
  return {
    mapData: groupedMapData,
    isLoading,
    error,
    allDataPoints,
    selectedLocation,
    setSelectedLocation,
    fetchMapData,
    geocodeAddress,
    getLocationStatistics,
    getDataPointById,
    filterDataPointsByType
  };
}

export default useMap;
